import classNames from 'classnames';
import Preact from 'preact';
import enhanceWithClickOutside from '../../helpers/clickOutside';
import testId from '../../helpers/testId';
import AnimatedChevron from '../AnimatedChevron';

export interface MenuItem {
  kind: 'item';
  content?: Preact.ComponentChild;
  url?: string;
  wrapperClassName?: string;
}
export const makeItem = ({
  content,
  url,
  wrapperClassName,
}: Pick<MenuItem, 'content' | 'url' | 'wrapperClassName'>): Item => ({
  kind: 'item',
  content,
  url,
  wrapperClassName,
});

export type Separator = { kind: 'separator'; className?: string };
export const makeSeparator = ({
  className,
}: { className?: string } = {}): Separator => ({
  kind: 'separator',
  className,
});

export type Item = MenuItem | Separator;

export interface RenderButton {
  (args: { isOpen: boolean; caret: Preact.ComponentChild }):
    | Preact.ComponentChild
    | Preact.ComponentChildren;
}

interface Props {
  onButtonClick?: (e: MouseEvent) => void;
  contents?: Item[];
  button?: string | RenderButton;
  isOpen?: boolean;
  className?: string;
  menuClassName?: string;
  menuCaretClassName?: string;
}

interface State {
  isOpen: boolean;
}

export const makeListItem = (item: Item, i: number) => {
  if (item.kind === 'separator') {
    return (
      <li
        key={`separator_${i}`}
        className={classNames(
          testId('menu__item', 'menu__item--separator'),
          'jg-chrome-dropdown-menu__separator',
          'jg-bdb jg-bd--gainsboro jg-space-mvsm jg-space-mvms@md',
          item.className,
        )}
      />
    );
  }

  return (
    <li
      key={`item_${i}`}
      className={classNames(
        testId('menu__item', 'menu__item--item'),
        item.wrapperClassName,
      )}
    >
      <a
        className={classNames(
          'jg-display-b',
          'jg-text--medium-regular jg-text-no-underline@hover',
          'jg-color--empress jg-color--nero@hover',
          'jg-space-pvxs jg-space-phsm',
          'jg-space-pvn@md jg-space-mvxs@md jg-space-phmd@md',
        )}
        href={item.url}
      >
        {item.content}
      </a>
    </li>
  );
};

class Menu extends Preact.Component<Props, State> {
  state = {
    isOpen: this.props.isOpen || false,
  };

  handleOpen = () => {
    this.setState({ isOpen: true });
  };

  handleCollapse = () => {
    this.setState({ isOpen: false });
  };

  // Handler for react-click-outside HOC
  handleClickOutside = () => {
    this.handleCollapse();
  };

  renderMenuCaret = () => {
    const { menuCaretClassName } = this.props;
    const { isOpen } = this.state;

    return (
      <div
        className={classNames(
          testId('menu__menu-caret'),
          'jg-display-b jg-position-absolute',
          'jg-chrome-dropdown-menu__caret',
          'jg-anim-fade-in jg-anim-slide-up',
          { 'jg-anim-fade-in--active jg-anim-slide-up--active': isOpen },
          menuCaretClassName,
        )}
      >
        <svg
          viewBox="0 0 18 9"
          className={classNames(
            'jg-chrome-dropdown-menu__caret-stroke',
            'jg-display-b jg-position-absolute',
            'jg-color--very-light-grey',
          )}
        >
          <path d="M 0 9 L 9 1 L 18 9" />
        </svg>
        <svg
          viewBox="0 0 20 9"
          className={classNames(
            'jg-chrome-dropdown-menu__caret-fill',
            'jg-display-b jg-position-absolute',
            'jg-color--very-light-grey',
          )}
        >
          <path d="M 0 10 L 10 1 L 20 10" />
        </svg>
      </div>
    );
  };

  onButtonClick = (e: MouseEvent) => {
    const { onButtonClick } = this.props;

    if (onButtonClick) {
      onButtonClick(e);
    }

    if (e.defaultPrevented) return;

    if (this.state.isOpen) {
      this.handleCollapse();
    } else {
      this.handleOpen();
    }
  };

  render() {
    const { contents = [], button, className, menuClassName } = this.props;
    const { isOpen } = this.state;

    const buttonStyles = classNames(
      testId('menu__button'),
      'jg-chrome-dropdown-menu__button',
      'jg-bdn jg-background--transparent jg-cursor-pointer',
      'jg-display-ib jg-position-relative jg-h-full jg-space-pvn jg-space-phxs',
      'jg-text--medium-regular',
      'jg-hover-group',
      {
        'jg-color--empress jg-color--nero@hover': !isOpen,
        'jg-color--current': isOpen,
      },
    );

    const menuListStyles = classNames(
      testId('menu__list'),
      'jg-chrome-dropdown-menu__content',
      'jg-position-absolute jg-w-full',
      'jg-space-pvsm jg-space-mtsm@md',
      'jg-bd--very-light-grey jg-bdv jg-bdh@md jg-br@md',
      'jg-background--white jg-shadow-md',
      'jg-anim-fade-in jg-anim-slide-up',
      { 'jg-anim-fade-in--active jg-anim-slide-up--active': isOpen },
      menuClassName,
    );

    const buttonCaret = (
      <AnimatedChevron
        isOpen={isOpen}
        className={classNames(
          testId('menu__button-caret'),
          'jg-chrome-dropdown-menu__button-caret',
          'jg-display-ib jg-align--vertical-middle',
          'jg-icon--medium-small@lg',
          {
            'jg-stroke--nobel jg-stroke--empress@group-hover': !isOpen,
            'jg-stroke--current': isOpen,
          },
        )}
      />
    );

    return (
      <div
        className={classNames(
          testId('menu'),
          'jg-chrome-dropdown-menu',
          'jg-text--pull-left jg-position-relative@md',
          className,
        )}
      >
        <button className={buttonStyles} onClick={this.onButtonClick}>
          {typeof button === 'string' && [
            <span className="jg-display-ib jg-align--vertical-center">
              {button}
            </span>,
            buttonCaret,
          ]}
          {typeof button === 'function' &&
            button({ caret: buttonCaret, isOpen })}
          {this.renderMenuCaret()}
        </button>

        <ul className={menuListStyles}>{contents.map(makeListItem)}</ul>
      </div>
    );
  }
}

const MenuWithClickOutside = enhanceWithClickOutside(Menu);
MenuWithClickOutside.displayName = 'Menu';

export default MenuWithClickOutside;
